import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const AUTOPLAY_FLOW = [
  "/schools",
  "/schools/student",
  "/schools/parent",
  "/schools/counsellor",
  "/schools/studios",
  "/schools/portfolio",
  "/schools/reports",
] as const;

const PACE_KEY = "wekit.schools.autoplay.pace";
const ON_KEY = "wekit.schools.autoplay.on";
const DEFAULT_PACE = 12; // seconds per step

export function useAutoplay() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [on, setOn] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(ON_KEY) === "1";
  });
  const [pace, setPace] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_PACE;
    const v = Number(localStorage.getItem(PACE_KEY));
    return Number.isFinite(v) && v >= 4 && v <= 60 ? v : DEFAULT_PACE;
  });
  const [progress, setProgress] = useState(0); // 0..1 within current step
  const startRef = useRef<number>(Date.now());

  const currentIdx = Math.max(
    0,
    AUTOPLAY_FLOW.findIndex((p) => (p === "/schools" ? pathname === p : pathname.startsWith(p)))
  );

  useEffect(() => {
    localStorage.setItem(ON_KEY, on ? "1" : "0");
  }, [on]);
  useEffect(() => {
    localStorage.setItem(PACE_KEY, String(pace));
  }, [pace]);

  // Reset timer whenever route changes or autoplay toggles
  useEffect(() => {
    startRef.current = Date.now();
    setProgress(0);
  }, [pathname, on, pace]);

  useEffect(() => {
    if (!on) return;
    const paceMs = pace * 1000;
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const p = Math.min(1, elapsed / paceMs);
      setProgress(p);
      if (p >= 1) {
        const next = AUTOPLAY_FLOW[(currentIdx + 1) % AUTOPLAY_FLOW.length];
        navigate(next);
      }
    };
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [on, pace, currentIdx, navigate]);

  const toggle = useCallback(() => setOn((v) => !v), []);
  const skip = useCallback(() => {
    const next = AUTOPLAY_FLOW[(currentIdx + 1) % AUTOPLAY_FLOW.length];
    navigate(next);
  }, [currentIdx, navigate]);
  const back = useCallback(() => {
    const prev = AUTOPLAY_FLOW[(currentIdx - 1 + AUTOPLAY_FLOW.length) % AUTOPLAY_FLOW.length];
    navigate(prev);
  }, [currentIdx, navigate]);

  // Keyboard: "A" toggles autoplay; "]" skip, "[" back — ignore when typing
  useEffect(() => {
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "a" || e.key === "A") {
        e.preventDefault();
        toggle();
      } else if (on && e.key === "]") {
        e.preventDefault();
        skip();
      } else if (on && e.key === "[") {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on, toggle, skip, back]);

  return {
    on,
    toggle,
    pace,
    setPace,
    progress,
    currentIdx,
    total: AUTOPLAY_FLOW.length,
    skip,
    back,
  };
}
